/**
 * Staged Items View
 *
 * Post-import reconciliation queue. Drives unlinked BudgetImportItems down
 * to zero by letting operators:
 *   - Accept a 100%-confidence auto-suggestion (bulk or one-by-one)
 *   - Search any Position and link manually
 *   - Create a brand-new F&O Position and link (for `New###` budget rows)
 *   - Dismiss rows that shouldn't import at all
 *   - Review + undo already-linked rows
 *
 * Excel export (read-only snapshot in Phase 1) is triggered from here too.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Edit3,
  ExternalLink,
  Info,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import {
  budgetService,
  organizationService,
  UnlinkedBudgetItem,
  UnlinkedSuggestion,
  Position,
  BudgetDispositionGroup,
  DISPOSITION_META,
} from '@/services/api/organizationService';
import { DispositionChip } from './DispositionChip';
import UnfundedStaffView from './UnfundedStaffView';

// ── Visual helpers ─────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  existing: 'Existing',
  existing_vacancy: 'Existing Vacancy',
  growth: 'Growth',
  new: 'New',
  remove: 'Remove',
  savings: 'Saving',
  special: 'Special',
};

const SOURCE_COLORS: Record<string, string> = {
  existing: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  existing_vacancy: 'text-amber-700 bg-amber-50 border-amber-200',
  growth: 'text-blue-700 bg-blue-50 border-blue-200',
  new: 'text-purple-700 bg-purple-50 border-purple-200',
  remove: 'text-gray-500 bg-gray-50 border-gray-200',
  savings: 'text-rose-700 bg-rose-50 border-rose-200',
  special: 'text-indigo-700 bg-indigo-50 border-indigo-200',
};

const STATUS_COLORS: Record<string, string> = {
  unmatched:    'text-red-600 bg-red-50 border-red-200',
  suggested:    'text-amber-700 bg-amber-50 border-amber-200',
  manual:       'text-blue-700 bg-blue-50 border-blue-200',
  auto_matched: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  dismissed:    'text-gray-400 bg-gray-50 border-gray-200',
};

// Payroll-type colour codes (chip + row badge). Hand-picked for at-a-glance
// distinction: PERM = blue (stable), ICA = purple (contractor), FIXED = teal.
const PAYROLL_COLORS: Record<string, string> = {
  PERM:  'text-blue-700 bg-blue-50 border-blue-200',
  ICA:   'text-purple-700 bg-purple-50 border-purple-200',
  FIXED: 'text-teal-700 bg-teal-50 border-teal-200',
};

function formatCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency', currency: 'ZAR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function avgMonthly(amounts: Record<number | string, number> | null | undefined): number {
  if (!amounts) return 0;
  const vals = Object.values(amounts).filter((v): v is number => typeof v === 'number' && v > 0);
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
}

// ── Component ──────────────────────────────────────────────────────────

type FilterMode = 'queue' | 'linked' | 'manual' | 'dismissed' | 'unfunded';

interface Props {
  budgetYear: number;
  /** Called after any mutation that may affect the parent budget summary
   *  (link, dismiss, undismiss, create-and-link, bulk link). Wired to
   *  loadSummary in BudgetTab so the top stats cards refresh. */
  onMutated?: () => void;
  /** Bridge-side totals per disposition group (ALL staged rows incl. linked /
   *  dismissed). When a group's queue count is lower — some rows already
   *  resolved — the tab renders "Duplicates (14 of 15)" so the bridge chip and
   *  the tab visibly reconcile instead of looking like a lost record. */
  groupTotals?: Partial<Record<BudgetDispositionGroup, number>>;
}

const StagedItemsView: React.FC<Props> = ({ budgetYear, onMutated, groupTotals }) => {
  const [items, setItems] = useState<UnlinkedBudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>('queue');
  const [search, setSearch] = useState('');
  // Payroll type filter — values come from the Excel column verbatim:
  // "PERM - Permanent", "ICA - Independent Contractor Agreement", "FIXED - Fixed Period".
  // Match by code prefix (PERM/ICA/FIXED) so future label tweaks don't break the filter.
  const [payrollFilter, setPayrollFilter] = useState<'all' | 'PERM' | 'ICA' | 'FIXED'>('all');
  const [excludeICACasual, setExcludeICACasual] = useState(false);
  const [dispGroupFilter, setDispGroupFilter] = useState<'all' | BudgetDispositionGroup>('all');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const [createPositionFor, setCreatePositionFor] = useState<UnlinkedBudgetItem | null>(null);
  const [searchLinkFor, setSearchLinkFor] = useState<UnlinkedBudgetItem | null>(null);

  const loadItems = useCallback(async () => {
    if (filter === 'unfunded') return;  // worker-grain view fetches its own data
    setLoading(true);
    try {
      const opts = filter === 'queue'
        ? { budgetYear }
        : filter === 'linked'
          ? { budgetYear, includeLinked: true, status: 'manual,auto_matched' }
          : filter === 'manual'
            // Human-made links only — the after-the-fact audit list when a
            // matching inconsistency is suspected (auto-matches excluded).
            ? { budgetYear, includeLinked: true, status: 'manual' }
            : { budgetYear, includeDismissed: true, status: 'dismissed' };
      const resp = await budgetService.getUnlinkedItems(opts);
      setItems(resp.items || []);
    } catch (e: any) {
      setFlash({ type: 'error', text: e?.message || 'Failed to load staged items' });
    } finally {
      setLoading(false);
    }
  }, [budgetYear, filter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Flash auto-dismiss
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  // Derived: searchable items + payroll-type filter applied client-side
  // (payroll values are short so filtering N≤2.5k items in JS is fine).
  // Position numbers are matched on linked_position.fo_position_id AND on
  // any of the suggested_position_ids[*].fo_position_id — so an operator
  // searching for "000100" finds rows linked OR suggested to that F&O code.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      const code = (i.payroll_type || '').split(/\s+/)[0].toUpperCase();
      if (payrollFilter !== 'all' && code !== payrollFilter) return false;
      if (excludeICACasual && (code === 'ICA' || code.startsWith('CAS'))) return false;
      if (dispGroupFilter !== 'all') {
        const meta = i.disposition ? DISPOSITION_META[i.disposition] : undefined;
        if ((meta?.group ?? 'quarantine') !== dispGroupFilter) return false;
      }
      if (!q) return true;
      const linkedFoPid = (i.linked_position?.fo_position_id || '').toLowerCase();
      const suggestedFoPids = (i.suggested_position_ids || [])
        .map((s) => (s.fo_position_id || '').toLowerCase());
      // EVERY visible badge is searchable: disposition (code, display label,
      // group), match status ("unmatched", "suggested", "manual"…), source
      // ("Existing Vacancy", "Growth"…), and the warning chips — so operators
      // can pull up everything wearing a given tag.
      const tagMeta = i.disposition ? DISPOSITION_META[i.disposition] : undefined;
      return (
        (i.job_title || '').toLowerCase().includes(q)
        || (i.employee_name || '').toLowerCase().includes(q)
        || (i.employee_code || '').toLowerCase().includes(q)
        || (i.budget_code || '').toLowerCase().includes(q)
        || (i.campus_code || '').toLowerCase().includes(q)
        || (i.department_code || '').toLowerCase().includes(q)
        || (i.payroll_type || '').toLowerCase().includes(q)
        || (i.disposition || '').toLowerCase().replace(/_/g, ' ').includes(q)
        || (tagMeta?.label || '').toLowerCase().includes(q)
        || (tagMeta?.group || '').toLowerCase().includes(q)
        || (i.match_status || '').toLowerCase().includes(q)
        || (SOURCE_LABELS[i.source_type] || i.source_type || '').toLowerCase().includes(q)
        || (i.dim_mismatch ? 'dims differ dimension mismatch' : '').includes(q)
        || (i.requires_position_creation ? 'needs position' : '').includes(q)
        || (i.title_mismatch ? 'title mismatch' : '').includes(q)
        || linkedFoPid.includes(q)
        || suggestedFoPids.some((p) => p.includes(q))
      );
    });
  }, [items, search, payrollFilter, excludeICACasual, dispGroupFilter]);

  // Virtualize the queue — the full filtered set (up to ~2.4k lines) scrolls
  // without rendering every row. Rows expand (suggestions), so heights are
  // dynamic and measured via measureElement.
  const listRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 52,
    overscan: 10,
  });

  // Disposition group counts (over the full queue) for the filter chips + bulk.
  const dispGroupCounts = useMemo(() => {
    const c: Record<string, number> = { all: items.length, funded: 0, duplicate: 0, demand: 0, adjustment: 0, quarantine: 0 };
    for (const it of items) {
      const meta = it.disposition ? DISPOSITION_META[it.disposition] : undefined;
      c[meta?.group ?? 'quarantine'] += 1;
    }
    return c;
  }, [items]);

  // "14 of 15" when the bridge holds more rows of a group than the queue shows
  // (the rest were already linked/dismissed) — keeps tab and bridge chip footing.
  const withGroupTotal = (grp: BudgetDispositionGroup, queueCount: number) => {
    const total = groupTotals?.[grp];
    return total != null && total > queueCount ? `${queueCount} of ${total}` : `${queueCount}`;
  };

  // Button count uses the SAME predicate as dismissAllDuplicates (role moves and
  // already-dismissed rows excluded) so the label promises what the click does.
  const clearableDuplicates = useMemo(() => items.filter((i) => {
    const meta = i.disposition ? DISPOSITION_META[i.disposition] : undefined;
    return meta?.group === 'duplicate' && i.disposition !== 'role_move' && i.match_status !== 'dismissed';
  }).length, [items]);

  // Stats are derived from `items` (full set) so the cards always reflect the
  // overall queue, not the filtered subset — gives operators a stable reference.
  const stats = useMemo(() => {
    const s = { unmatched: 0, suggested: 0, linked: 0, dismissed: 0, high_conf: 0 };
    for (const it of items) {
      if (it.match_status === 'unmatched') s.unmatched += 1;
      else if (it.match_status === 'suggested') {
        s.suggested += 1;
        const top = it.suggested_position_ids?.[0];
        // ≥0.95 = exact Campus|Dept|Faculty bucket (0.6) + title fuzzy ≥87.5%.
        // The generic-suffix guard caps unrelated titles at 0.8 total, so
        // nothing role-ambiguous can reach this bar.
        if (top && top.score >= 0.95) s.high_conf += 1;
      } else if (it.match_status === 'manual' || it.match_status === 'auto_matched') s.linked += 1;
      else if (it.match_status === 'dismissed') s.dismissed += 1;
    }
    return s;
  }, [items]);

  // Payroll-type counts (within the current Queue/Linked/Dismissed view) so
  // the chip labels can show "PERM (1469)" etc.
  const payrollCounts = useMemo(() => {
    const c = { all: items.length, PERM: 0, ICA: 0, FIXED: 0 };
    for (const it of items) {
      const code = (it.payroll_type || '').split(/\s+/)[0].toUpperCase();
      if (code === 'PERM') c.PERM += 1;
      else if (code === 'ICA') c.ICA += 1;
      else if (code === 'FIXED') c.FIXED += 1;
    }
    return c;
  }, [items]);

  // ── Actions ──────────────────────────────────────────────────────

  const withBusy = async <T,>(itemId: number, fn: () => Promise<T>) => {
    setBusyIds((prev) => { const n = new Set(prev); n.add(itemId); return n; });
    try { return await fn(); }
    finally { setBusyIds((prev) => { const n = new Set(prev); n.delete(itemId); return n; }); }
  };

  const acceptTopSuggestion = async (item: UnlinkedBudgetItem) => {
    // Prefer the matcher's deduped pick (item.position_id) over the per-row
    // top suggestion. For dim-fallback rows in the same combo, the top
    // suggestion is shared but the matcher's Pass 2 dedup spreads them
    // across unique vacancies — accepting the top would over-link.
    const matcherPick = item.position_id
      ? item.suggested_position_ids?.find(s => s.position_id === item.position_id)
      : null;
    const target = matcherPick ?? item.suggested_position_ids?.[0];
    if (!target) return;
    await withBusy(item.id, async () => {
      try {
        await budgetService.linkBudgetItem(item.id, target.position_id);
        setFlash({ type: 'success', text: `Linked "${item.job_title}" → ${target.fo_position_id || target.position_id}` });
        await loadItems();
        onMutated?.();
      } catch (e: any) {
        setFlash({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Link failed' });
      }
    });
  };

  const acceptSpecificSuggestion = async (item: UnlinkedBudgetItem, sug: UnlinkedSuggestion) => {
    await withBusy(item.id, async () => {
      try {
        await budgetService.linkBudgetItem(item.id, sug.position_id);
        setFlash({ type: 'success', text: `Linked "${item.job_title}" → ${sug.fo_position_id || sug.position_id}` });
        await loadItems();
        onMutated?.();
      } catch (e: any) {
        setFlash({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Link failed' });
      }
    });
  };

  const dismissItem = async (item: UnlinkedBudgetItem, reason?: string) => {
    await withBusy(item.id, async () => {
      try {
        await budgetService.dismissBudgetItem(item.id, reason);
        setFlash({ type: 'success', text: `Dismissed "${item.job_title || item.budget_code}"` });
        await loadItems();
        onMutated?.();
      } catch (e: any) {
        setFlash({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Dismiss failed' });
      }
    });
  };

  const undismissItem = async (item: UnlinkedBudgetItem) => {
    await withBusy(item.id, async () => {
      try {
        await budgetService.undismissBudgetItem(item.id);
        setFlash({ type: 'success', text: `Undismissed "${item.job_title || item.budget_code}"` });
        await loadItems();
        onMutated?.();
      } catch (e: any) {
        setFlash({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Undismiss failed' });
      }
    });
  };

  const acceptAllHighConfidence = async () => {
    // Use the matcher's assigned position_id (which is deduped per-row)
    // rather than suggested_position_ids[0] which is identical across rows
    // in the same dim combo and would over-link the same position.
    const seen = new Set<number>();
    const links = items
      .filter((i) => i.match_status === 'suggested' && i.suggested_position_ids?.[0]?.score >= 0.95)
      .map((i) => ({ item_id: i.id, position_id: i.position_id ?? i.suggested_position_ids[0].position_id }))
      .filter((l) => l.position_id != null)
      // In-batch dedup: when the assigned id is absent and two lines fall back
      // to the same top suggestion, only the first may claim the seat.
      .filter((l) => (seen.has(l.position_id!) ? false : (seen.add(l.position_id!), true)));
    if (!links.length) {
      setFlash({ type: 'error', text: 'No ≥95% suggestions to accept' });
      return;
    }
    if (!window.confirm(`Accept ${links.length} suggestions at ≥95% confidence? (exact dimension match + near-identical title — the generic-title guard keeps ambiguous roles below this bar)`)) return;
    setBulkRunning(true);
    try {
      const res = await budgetService.linkBudgetItemsBulk(links);
      setFlash({ type: 'success', text: `Linked ${res.linked}/${res.total}` });
      await loadItems();
      onMutated?.();
    } catch (e: any) {
      setFlash({ type: 'error', text: e?.response?.data?.detail || e?.message || 'Bulk link failed' });
    } finally {
      setBulkRunning(false);
    }
  };

  // Bulk-resolve a whole disposition class. Duplicates/role-moves/filled-vacancies
  // are funded by their winner line, so the operator can clear them in one click.
  const dismissAllDuplicates = async () => {
    const dups = items.filter((i) => {
      const meta = i.disposition ? DISPOSITION_META[i.disposition] : undefined;
      // role_move is intentionally EXCLUDED — never auto-collapse a genuine move.
      return meta?.group === 'duplicate' && i.disposition !== 'role_move'
        && i.match_status !== 'dismissed';
    });
    if (!dups.length) {
      setFlash({ type: 'error', text: 'No duplicate / filled-vacancy lines to clear' });
      return;
    }
    if (!window.confirm(
      `Dismiss ${dups.length} duplicate / filled-vacancy line(s)? Their seat is already funded by the winner line; role moves are left for you to decide.`
    )) return;
    setBulkRunning(true);
    try {
      let ok = 0;
      for (const it of dups) {
        try { await budgetService.dismissBudgetItem(it.id, `auto: ${it.disposition}`); ok += 1; } catch { /* keep going */ }
      }
      setFlash({ type: 'success', text: `Dismissed ${ok}/${dups.length} duplicate lines` });
      await loadItems();
      onMutated?.();
    } finally {
      setBulkRunning(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await budgetService.exportBudgetItems(budgetYear, { includeLinked: true, includeDismissed: false });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget_${budgetYear}_staged_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setFlash({ type: 'error', text: e?.message || 'Export failed' });
    }
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatCard label="Unmatched" value={stats.unmatched} color="text-red-600" />
        <StatCard label="Suggested" value={stats.suggested} color="text-amber-600" sub={`${stats.high_conf} at ≥95%`} />
        <StatCard label="Linked" value={stats.linked} color="text-emerald-600" />
        <StatCard label="Dismissed" value={stats.dismissed} color="text-gray-400" />
        <StatCard label="Total" value={items.length} color="text-slate-700" />
      </div>

      {/* Flash */}
      {flash && (
        <div className={cn(
          "rounded p-2 text-xs flex items-center gap-2",
          flash.type === 'success'
            ? "bg-emerald-50 border border-emerald-100 text-emerald-700"
            : "bg-red-50 border border-red-100 text-red-700",
        )}>
          {flash.type === 'success' ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
          {flash.text}
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setFlash(null)}>
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Filter + actions */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded border p-0.5 bg-gray-50">
            <FilterChip active={filter === 'queue'} onClick={() => setFilter('queue')} label="Queue" />
            <FilterChip active={filter === 'linked'} onClick={() => setFilter('linked')} label="Linked" />
            <FilterChip active={filter === 'manual'} onClick={() => setFilter('manual')} label="Manual links" />
            <FilterChip active={filter === 'dismissed'} onClick={() => setFilter('dismissed')} label="Dismissed" />
            <FilterChip active={filter === 'unfunded'} onClick={() => setFilter('unfunded')} label="Unfunded staff" />
          </div>
          {filter !== 'unfunded' && (<>
          <div className="flex gap-1 rounded border p-0.5 bg-gray-50">
            <FilterChip active={payrollFilter === 'all'} onClick={() => setPayrollFilter('all')} label={`All (${payrollCounts.all})`} />
            <FilterChip active={payrollFilter === 'PERM'} onClick={() => setPayrollFilter('PERM')} label={`PERM (${payrollCounts.PERM})`} />
            <FilterChip active={payrollFilter === 'ICA'} onClick={() => setPayrollFilter('ICA')} label={`ICA (${payrollCounts.ICA})`} />
            <FilterChip active={payrollFilter === 'FIXED'} onClick={() => setPayrollFilter('FIXED')} label={`FIXED (${payrollCounts.FIXED})`} />
          </div>
          <FilterChip active={excludeICACasual} onClick={() => setExcludeICACasual((v) => !v)} label="Exclude ICA / Casual" />
          {/* Disposition group filter — only meaningful once a new-matcher import has run */}
          {dispGroupCounts.funded + dispGroupCounts.duplicate + dispGroupCounts.demand + dispGroupCounts.adjustment > 0 && (
            <div className="flex gap-1 rounded border p-0.5 bg-gray-50">
              <FilterChip active={dispGroupFilter === 'all'} onClick={() => setDispGroupFilter('all')} label={`All (${dispGroupCounts.all})`} />
              <FilterChip active={dispGroupFilter === 'duplicate'} onClick={() => setDispGroupFilter('duplicate')} label={`Duplicates (${withGroupTotal('duplicate', dispGroupCounts.duplicate)})`} />
              <FilterChip active={dispGroupFilter === 'demand'} onClick={() => setDispGroupFilter('demand')} label={`Demand (${withGroupTotal('demand', dispGroupCounts.demand)})`} />
              <FilterChip active={dispGroupFilter === 'quarantine'} onClick={() => setDispGroupFilter('quarantine')} label={`Quarantine (${withGroupTotal('quarantine', dispGroupCounts.quarantine)})`} />
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by title, name, code, campus, department, payroll, position no, tag…"
                className="pl-7 h-8 text-xs"
              />
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadItems} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1", loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-3.5 w-3.5 mr-1" /> Export
          </Button>
          {filter === 'queue' && stats.high_conf > 0 && (
            <Button size="sm" onClick={acceptAllHighConfidence} disabled={bulkRunning}
              title="Top suggestion scored ≥95%: exact Campus|Dept|Faculty match + near-identical title. Ambiguous roles are capped below this bar by the generic-title guard.">
              {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
              Accept all ≥95% ({stats.high_conf})
            </Button>
          )}
          {filter === 'queue' && (clearableDuplicates > 0) && (
            <Button variant="outline" size="sm" onClick={dismissAllDuplicates} disabled={bulkRunning}
              title="Dismiss duplicate & filled-vacancy lines (winner funds the seat). Role moves are left for you.">
              {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Layers className="h-3.5 w-3.5 mr-1" />}
              Clear duplicates ({clearableDuplicates})
            </Button>
          )}
          </>)}
        </CardContent>
      </Card>

      {/* Unfunded staff — worker-grain inverse of the RF1 queue */}
      {filter === 'unfunded' ? (
        <UnfundedStaffView year={budgetYear} />
      ) : (
      /* Items list */
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-gray-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
              Loading staged items…
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              {filter === 'queue' ? 'Queue is empty — all items reconciled.' : `No ${filter} items for ${budgetYear}.`}
            </div>
          ) : (
            <div ref={listRef} className="max-h-[68vh] overflow-y-auto text-xs">
              <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                {rowVirtualizer.getVirtualItems().map((vRow) => {
                  const item = visibleItems[vRow.index];
                  return (
                    <div
                      key={item.id}
                      data-index={vRow.index}
                      ref={rowVirtualizer.measureElement}
                      className="absolute left-0 top-0 w-full border-b"
                      style={{ transform: `translateY(${vRow.start}px)` }}
                    >
                      <StagedRow
                        item={item}
                        expanded={expanded.has(item.id)}
                        busy={busyIds.has(item.id)}
                        onToggle={() => toggleExpand(item.id)}
                        onAcceptTop={() => acceptTopSuggestion(item)}
                        onAcceptSpecific={(s) => acceptSpecificSuggestion(item, s)}
                        onDismiss={() => dismissItem(item)}
                        onUndismiss={() => undismissItem(item)}
                        onSearchAndLink={() => setSearchLinkFor(item)}
                        onCreatePosition={() => setCreatePositionFor(item)}
                        filter={filter === 'manual' ? 'linked' : filter}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Create Position modal */}
      {createPositionFor && (
        <CreatePositionDialog
          item={createPositionFor}
          onClose={() => setCreatePositionFor(null)}
          onCreated={async () => {
            setCreatePositionFor(null);
            setFlash({ type: 'success', text: 'Position created in F&O and linked' });
            await loadItems();
            onMutated?.();
          }}
          onError={(msg) => setFlash({ type: 'error', text: msg })}
        />
      )}

      {/* Position Search modal */}
      {searchLinkFor && (
        <PositionSearchDialog
          item={searchLinkFor}
          onClose={() => setSearchLinkFor(null)}
          onLinked={async (fo_pid) => {
            setSearchLinkFor(null);
            setFlash({ type: 'success', text: `Linked "${searchLinkFor.job_title}" → ${fo_pid}` });
            await loadItems();
            onMutated?.();
          }}
          onError={(msg) => setFlash({ type: 'error', text: msg })}
        />
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number; color: string; sub?: string }> = ({ label, value, color, sub }) => (
  <Card>
    <CardContent className="p-3">
      <div className={cn("text-xl font-bold", color)}>{value.toLocaleString()}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const FilterChip: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={cn(
      "px-2.5 py-1 rounded text-xs font-medium transition",
      active ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700",
    )}
  >
    {label}
  </button>
);

interface StagedRowProps {
  item: UnlinkedBudgetItem;
  expanded: boolean;
  busy: boolean;
  filter: FilterMode;
  onToggle: () => void;
  onAcceptTop: () => void;
  onAcceptSpecific: (s: UnlinkedSuggestion) => void;
  onDismiss: () => void;
  onUndismiss: () => void;
  onSearchAndLink: () => void;
  onCreatePosition: () => void;
}

const StagedRow: React.FC<StagedRowProps> = ({
  item, expanded, busy, filter,
  onToggle, onAcceptTop, onAcceptSpecific, onDismiss, onUndismiss, onSearchAndLink, onCreatePosition,
}) => {
  const avg = avgMonthly(item.monthly_amounts as any);
  // `top` drives both the displayed score % and the visibility of the
  // "Accept" button. Prefer the matcher's deduped pick (item.position_id)
  // over the per-row top suggestion so the displayed score and the
  // accepted position stay aligned (see acceptTopSuggestion for the
  // matching logic).
  const matcherPick = item.position_id
    ? item.suggested_position_ids?.find(s => s.position_id === item.position_id)
    : null;
  const top = matcherPick ?? item.suggested_position_ids?.[0];
  const hasSuggestions = (item.suggested_position_ids?.length ?? 0) > 0;
  // Financial-dimension drift (RF1 vs the linked seat's F&O dims) — server-computed
  // across all three fields, shown in EVERY view (queue + linked): drift on a
  // confirmed link is exactly what needs correcting on one side or the other.
  const dimMismatch = item.dim_mismatch;

  return (
    <div className="py-2 px-3 hover:bg-gray-50">
      <div className="flex items-center gap-2">
        <button
          onClick={onToggle}
          className="text-gray-400 hover:text-gray-600"
          disabled={!hasSuggestions && !item.linked_position}
        >
          {(hasSuggestions || item.linked_position) ? (
            expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
          ) : <span className="inline-block w-3.5" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-slate-900">{item.job_title || '—'}</span>
            <DispositionChip disposition={item.disposition} />
            {item.employee_name && <span className="text-gray-500">· {item.employee_name}</span>}
            {item.employee_code && <span className="text-gray-400">· {item.employee_code}</span>}
            {item.budget_code && <Badge variant="outline" className="text-[9px] border-gray-200">{item.budget_code}</Badge>}
            <Badge variant="outline" className={cn("text-[9px]", SOURCE_COLORS[item.source_type])}>
              {SOURCE_LABELS[item.source_type] || item.source_type}
            </Badge>
            {item.payroll_type && (
              <Badge variant="outline" className={cn("text-[9px]", PAYROLL_COLORS[(item.payroll_type.split(/\s+/)[0] || '').toUpperCase()] || 'border-gray-200 text-gray-500')}>
                {(item.payroll_type.split(/\s+/)[0] || '').toUpperCase()}
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-[9px]", STATUS_COLORS[item.match_status])}>
              {item.match_status}
              {item.match_status === 'suggested' && top && ` ${Math.round((top.score ?? 0) * 100)}%`}
            </Badge>
            {dimMismatch && (
              <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200"
                title={`Budget vs F&O financial dimensions differ — RF1: ${dimMismatch.rf1.campus}|${dimMismatch.rf1.department}|${dimMismatch.rf1.faculty} vs F&O: ${dimMismatch.fo.campus}|${dimMismatch.fo.department}|${dimMismatch.fo.faculty}`}>
                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Dims differ: {dimMismatch.fields.join(', ')}
              </Badge>
            )}
            {item.requires_position_creation && (
              <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200" title="Placeholder row — no vacant F&O position was available at import. Use 'New' to create one.">
                <Plus className="h-2.5 w-2.5 mr-0.5" /> Needs Position
              </Badge>
            )}
            {item.title_mismatch && (
              <Badge
                variant="outline"
                className="text-[9px] text-rose-700 bg-rose-50 border-rose-200"
                title={`Finance worksheet says "${item.title_mismatch.budget_title}" but the linked F&O position is "${item.title_mismatch.position_title}" (${Math.round(item.title_mismatch.similarity * 100)}% similar). Verify whether the position assignment is wrong, or the worksheet has the role mislabelled.`}
              >
                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Title mismatch
              </Badge>
            )}
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {item.campus_code && <>Campus {item.campus_code} · </>}
            {item.department_code && <>Dept {item.department_code} · </>}
            {item.faculty_code && <>Faculty {item.faculty_code} · </>}
            Avg/mo {formatCurrency(avg)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {filter === 'queue' && (
            <>
              {top && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onAcceptTop} disabled={busy}>
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-0.5" />}
                  Accept
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSearchAndLink} disabled={busy}>
                <Search className="h-3 w-3 mr-0.5" /> Search
              </Button>
              {(item.source_type === 'new' || item.source_type === 'growth' || item.requires_position_creation) && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onCreatePosition} disabled={busy}>
                  <Plus className="h-3 w-3 mr-0.5" /> New
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 text-xs text-gray-500" onClick={onDismiss} disabled={busy}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
          {filter === 'linked' && item.linked_position && (
            <>
              <span className="text-[10px] text-gray-500 mr-1">
                → {item.linked_position.fo_position_id || item.linked_position.id}
                {item.linked_position.is_vacant ? ' (vacant)' : ` · ${item.linked_position.incumbent_name || 'occupied'}`}
              </span>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-gray-500" onClick={onDismiss} disabled={busy} title="Unlink and dismiss">
                <Undo2 className="h-3 w-3" />
              </Button>
            </>
          )}
          {filter === 'dismissed' && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onUndismiss} disabled={busy}>
              <Undo2 className="h-3 w-3 mr-0.5" /> Restore
            </Button>
          )}
        </div>
      </div>

      {/* Disposition explainer — what this situation IS and what Select/Dismiss
          actually do (budget-side only; people and F&O are never changed). */}
      {expanded && item.disposition && DISPOSITION_META[item.disposition]?.hint && (
        <div className="mt-2 ml-6 flex items-start gap-1.5 rounded border border-sky-100 bg-sky-50 p-2 text-[11px] text-sky-900">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{DISPOSITION_META[item.disposition]!.hint}</span>
        </div>
      )}

      {/* Expanded suggestions */}
      {expanded && hasSuggestions && filter === 'queue' && (
        <div className="mt-2 ml-6 p-2 bg-gray-50 rounded border text-xs">
          <div className="font-medium text-gray-600 mb-1">All suggestions ({item.suggested_position_ids.length})</div>
          <div className="space-y-1">
            {item.suggested_position_ids.map((sug, idx) => (
              <div key={idx} className="flex items-center gap-2 p-1.5 bg-white rounded border" title={sug.match_reason || ''}>
                <span className="font-mono text-[10px] text-gray-500 w-16">{sug.fo_position_id || sug.position_id}</span>
                <span className="min-w-0 flex-1">
                  {sug.title || '—'}
                  {/* WHY it scored what it did — without this, a person-history
                      95% ("their real F&O title matches this seat") is
                      indistinguishable from a dims+title 95%. */}
                  {sug.match_reason && (
                    <span className="block truncate text-[10px] text-gray-400">{sug.match_reason}</span>
                  )}
                </span>
                {sug.is_vacant ? (
                  <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200">Vacant</Badge>
                ) : (
                  <span className="text-[10px] text-gray-400">{sug.incumbent_name || 'occupied'}</span>
                )}
                <span className="text-[10px] text-gray-500 w-12 text-right">{Math.round((sug.score ?? 0) * 100)}%</span>
                <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => onAcceptSpecific(sug)} disabled={busy}>
                  Select
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expanded linked position */}
      {expanded && item.linked_position && filter === 'linked' && (
        <div className="mt-2 ml-6 p-2 bg-blue-50 rounded border border-blue-100 text-xs">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-3 w-3" />
            Linked to <span className="font-mono">{item.linked_position.fo_position_id}</span>
            · {item.linked_position.title}
            · {item.linked_position.campus_code || '—'} / {item.linked_position.department || '—'}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Create Position Dialog ────────────────────────────────────────

const CreatePositionDialog: React.FC<{
  item: UnlinkedBudgetItem;
  onClose: () => void;
  onCreated: () => void;
  onError: (msg: string) => void;
}> = ({ item, onClose, onCreated, onError }) => {
  const [foPositionId, setFoPositionId] = useState('');
  const [title, setTitle] = useState(item.job_title || '');
  const [jobId, setJobId] = useState('');
  const [titleId, setTitleId] = useState('');
  const [deptNumber, setDeptNumber] = useState(item.department_code || '');
  const [description, setDescription] = useState(item.job_title || '');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = foPositionId.trim() && title.trim() && jobId.trim() && deptNumber.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await budgetService.createPositionAndLink({
        item_id: item.id,
        position_id: foPositionId.trim(),
        title: title.trim(),
        job_id: jobId.trim(),
        title_id: titleId.trim() || undefined,
        department_number: deptNumber.trim(),
        description: description.trim() || undefined,
      });
      onCreated();
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Create failed');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create F&O Position</DialogTitle>
          <DialogDescription>
            Creates a new Position in F&O (HcmPositionEntity) via OData, inserts it locally, then links this budget row.
            Budget row: <span className="font-medium">{item.job_title}</span> ({item.budget_code})
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-xs">
          <Field label="F&O Position ID *" hint="Must be unique. Operator assigns — e.g. next numeric or follow your naming scheme.">
            <Input value={foPositionId} onChange={(e) => setFoPositionId(e.target.value)} placeholder="e.g. 004500" />
          </Field>
          <Field label="Title *" hint="Human-readable position title (stored on Position.title locally)">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="JobId *" hint="The Role Master code (copy from the Roles tab) — must exist in F&O HcmJob. The Title carries the human name.">
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} placeholder="e.g. RM-0042" />
          </Field>
          <Field label="TitleId" hint="Optional. F&O HcmTitle.TitleId. Leave blank if not yet defined.">
            <Input value={titleId} onChange={(e) => setTitleId(e.target.value)} placeholder="e.g. Lecturer" />
          </Field>
          <Field label="Department Number *" hint="F&O HcmDepartment code. Prefilled from budget row's department_code.">
            <Input value={deptNumber} onChange={(e) => setDeptNumber(e.target.value)} />
          </Field>
          <Field label="Description" hint="Optional. Defaults to title.">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="rounded border border-amber-100 bg-amber-50 p-2 text-[11px] text-amber-800">
            F&O-wide defaults applied: PositionType=FT Regular · PayCycle=Monthly · LegalEntity=eduv · FTE=1.0 · IsPrimary=Yes.
            Retirement date: 2154-12-31 (F&O "forever" sentinel).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            Create & Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium mb-1">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

// ── Position Search Dialog ────────────────────────────────────────

const PositionSearchDialog: React.FC<{
  item: UnlinkedBudgetItem;
  onClose: () => void;
  onLinked: (foPid: string) => void;
  onError: (msg: string) => void;
}> = ({ item, onClose, onLinked, onError }) => {
  const [query, setQuery] = useState(item.job_title || '');
  const [results, setResults] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);

  // Debounced search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await organizationService.listPositions({ query: q, limit: 40 });
        if (!cancelled) setResults(data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const handleLink = async (pos: Position) => {
    setLinkingId(pos.id);
    try {
      await budgetService.linkBudgetItem(item.id, pos.id);
      onLinked(pos.fo_position_id || String(pos.id));
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Link failed');
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Search and Link Position</DialogTitle>
          <DialogDescription>
            Pick any Position to link this budget row. Budget row: <span className="font-medium">{item.job_title}</span>
            {item.employee_name && <> · {item.employee_name}</>}
            {item.budget_code && <> · {item.budget_code}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title or position code…" autoFocus />
          <div className="border rounded max-h-[400px] overflow-auto text-xs">
            {loading ? (
              <div className="p-6 text-center text-gray-400"><Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" /> Searching…</div>
            ) : results.length === 0 ? (
              <div className="p-6 text-center text-gray-400">
                {query.trim().length < 2 ? 'Type at least 2 characters.' : 'No matches.'}
              </div>
            ) : (
              <div className="divide-y">
                {results.map((pos) => (
                  <div key={pos.id} className="p-2 flex items-center gap-2 hover:bg-gray-50">
                    <span className="font-mono text-[10px] text-gray-500 w-16">{pos.fo_position_id || pos.id}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{pos.title}</div>
                      <div className="text-[10px] text-gray-500">
                        {pos.campus_code || '—'} · {pos.functional_area || pos.job_family || '—'}
                        {pos.incumbent_user_id ? ` · ${pos.incumbent_name || 'occupied'}` : ' · vacant'}
                      </div>
                    </div>
                    <Button size="sm" className="h-7 text-xs" onClick={() => handleLink(pos)} disabled={linkingId !== null}>
                      {linkingId === pos.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Link'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StagedItemsView;
